<template>
  <div class="chart-wrapper">
    <div v-if="isChartEmpty || !zonePresent" class="no-data-message">{{ $t('charts.productZones.noData') }}</div>

    <!-- keep your loader while computing -->
    <div v-else-if="isLoading" class="loader-overlay"><div class="loader"></div></div>

    <!-- safe init: only mounts echarts when visible & sized -->
    <PrioChart
      v-else
      :option="option"
      :active="active"
      :height="300"
      name="ProductTotalDuration"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import PrioChart from '@/components/ui/PrioChart.vue'          // <-- use wrapper
import { use } from 'echarts/core'
import { BarChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, TitleComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { useI18n } from 'vue-i18n'
import { currentLanguage } from '@/i18n'
import { formatDuration } from '@/i18n/format'
use([BarChart, GridComponent, TooltipComponent, TitleComponent, CanvasRenderer])

export default {
  name: 'ChartsFixedWidgetsProductTotalDuration',
  components: { PrioChart },
  props: {
    totalDurationPerZone: { type: Object, required: true },
    zonePresent: { type: Boolean, required: true },
    // pass this from parent: activeTab === 'data-foundation-layer'
    active: { type: Boolean, default: true }
  },
  setup(props){
    const { t } = useI18n()
    const isLoading = ref(true)

    const colorPalette = ['#425461','#4F7EB3','#B2C149','#9FC8E3','#6C9046','#6B92C6']
    const getColorForZone = (zone,i=0)=>{
      const h=[...zone].reduce((s,c)=>s+c.charCodeAt(0),0); return colorPalette[(h+i)%colorPalette.length]
    }

    const labels = computed(()=> Object.keys(props.totalDurationPerZone || {}))
    const values = computed(()=> labels.value.map(z => props.totalDurationPerZone?.[z] ?? 0))

    const isChartEmpty = computed(()=> !labels.value.length || values.value.every(v=>!v))

    const fmtAxis = s=>{
      if(s<60) return `${s.toFixed(0)} sec`
      if(s<3600) return `${Math.floor(s/60)} min`
      return `${Math.floor(s/3600)} ${t('charts.units.h')}`
    }
    const fmtTip = s=>{
      if(currentLanguage() !== 'en') return formatDuration(s)   // Dutch: "1 uur 5 min"
      if(s<60) return `${s.toFixed(0)} sec`
      if(s<3600){const m=Math.floor(s/60), r=s%60; return `${m} min ${r.toFixed(0)} sec`}
      const h=Math.floor(s/3600), m=Math.floor((s%3600)/60), r=s%60
      return `${h} h ${m} min ${r.toFixed(0)} sec`
    }

    const option = computed(()=>({
      title:{ text:t('charts.productZones.totalTitle'), left:'center', textStyle:{color:'grey',fontSize:12}},
      grid:{ containLabel:true, top:'20%', height:'80%', right:'12%' },
      tooltip:{ trigger:'axis', formatter:({0:p})=>fmtTip(p.value) },
      xAxis:{ type:'category', data:labels.value, axisLabel:{ rotate:45, fontSize:10 } },
      yAxis:{ type:'value', min:0, axisLabel:{ formatter:fmtAxis }, name:t('charts.productZones.totalAxis') },
      series:[{
        type:'bar',
        data: labels.value.map((z,i)=>({ value: values.value[i]||0, itemStyle:{ color:getColorForZone(z,i) }}))
      }]
    }))

    onMounted(()=>{ isLoading.value = false })
    watch(()=>props.totalDurationPerZone, ()=>{ isLoading.value=false })

    return { isLoading, option, isChartEmpty }
  }
}
</script>

<style scoped>
.chart-wrapper{ width:100%; position:relative; }
.no-data-message{ color:gray; text-align:center; margin:20px 0 }
.loader-overlay{ position:absolute; inset:0; display:flex; align-items:center; justify-content:center; background:rgba(255,255,255,.8)}
.loader{ width:24px; aspect-ratio:1; border-radius:50%; background:#B2C149; animation:spin 1s linear infinite }
@keyframes spin{ to{ transform:rotate(1turn) } }
</style>